/**
 * ChangeImpacts — list page for CR-2026-DC-IMPACT-REVIEW (D-032 §10).
 */

import React, { useEffect, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { prettyStatus } from "../../components/PaperForm/styles";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Container, Typography, Stack, Button, Paper, Chip, Alert,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TextField, MenuItem, Breadcrumbs, Link as MuiLink,
} from "@mui/material";
import { HomeRounded, AddRounded, WarningAmberRounded } from "@mui/icons-material";

import { listImpacts, IMPACT_STATUSES } from "../../utils/changeImpactsApi";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLOR = {
  OPEN:          { bg: "#fff3cd", color: "#664d03" },
  IN_REVIEW:     { bg: "#cfe2ff", color: "#052c65" },
  DISPOSITIONED: { bg: "#d1e7dd", color: "#0a3622" },
  HELD:          { bg: "#f8d7da", color: "#58151c" },
  CLOSED:        { bg: "#e9ecef", color: "#6c757d" },
  CANCELLED:     { bg: "#e9ecef", color: "#6c757d" },
};

const RISK_COLOR = {
  LOW:      { bg: "#d1e7dd", color: "#0a3622" },
  MEDIUM:   { bg: "#fff3cd", color: "#664d03" },
  HIGH:     { bg: "#fd7e14", color: "#ffffff" },
  CRITICAL: { bg: "#dc3545", color: "#ffffff" },
};

export default function ChangeImpacts() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const jobIdParam = params.get("job_id") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [holdFilter, setHoldFilter] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null); setNotAvailable(false);
    const filters = {};
    if (jobIdParam) filters.job_id = jobIdParam;
    if (statusFilter && statusFilter !== "ALL") filters.status = statusFilter;
    if (holdFilter) filters.hold_active = true;
    listImpacts(filters)
      .then((data) => { if (!cancelled) setRows((data && data.rows) || []); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 404 || status === 501) setNotAvailable(true);
        else setError(err?.response?.data?.error || err?.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobIdParam, statusFilter, holdFilter]);

  return (
    <>
      <Helmet><title>Change Impact Reviews | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MuiLink href="/" color="inherit" underline="hover">
            <Stack direction="row" alignItems="center" spacing={0.5}><HomeRounded fontSize="small" /> <span>Home</span></Stack>
          </MuiLink>
          <Typography color="text.primary">Change Impact Reviews</Typography>
        </Breadcrumbs>

        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Change Impact Reviews</Typography>
          <Button variant="contained" startIcon={<AddRounded />}
            onClick={() => navigate(jobIdParam ? `/change-impacts/new?job_id=${jobIdParam}` : "/change-impacts/new")}>
            New Impact Review
          </Button>
        </Stack>
        <PageGuide
          why="When a drawing revision lands, find out which already-built or shipped pieces it affects — before the change quietly creates rework or a field problem."
          who="The QM and PM, assessing which built or shipped pieces a revision affects."
          what="A review that traces a new revision's impact on in-progress, shipped, and installed pieces."
          how="Start an impact review against the changed drawing, assess the affected pieces, and record the disposition."
          when="Whenever a new drawing revision affects work that's already underway or out the door."
          standard="AISC Drawing Control Ch. 7/8/9 · AISC 303 §A.4"
        />

        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField select label="Status" size="small" sx={{ minWidth: 200 }}
              value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="ALL">All statuses</MenuItem>
              {IMPACT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select label="Hold filter" size="small" sx={{ minWidth: 200 }}
              value={holdFilter ? "1" : "0"} onChange={(e) => setHoldFilter(e.target.value === "1")}>
              <MenuItem value="0">All</MenuItem>
              <MenuItem value="1">Active hold only</MenuItem>
            </TextField>
          </Stack>
        </Paper>

        {notAvailable && <Alert severity="warning" icon={<WarningAmberRounded />} sx={{ mb: 2 }}>
          This feature isn't available yet — please contact your administrator.
        </Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <SmartTable tableId="change-impacts" lockedColumns={["ID", "Document → Rev", "Status", "Disposition", "Hold"]}>
            <TableHead><TableRow>
              <TableCell>ID</TableCell><TableCell>Job</TableCell><TableCell>Document → Rev</TableCell>
              <TableCell>Risk</TableCell><TableCell>Status</TableCell><TableCell>Disposition</TableCell>
              <TableCell>Hold</TableCell><TableCell>Created</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#6c757d" }}>Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && !notAvailable && !error && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: "#6c757d" }}>
                  No impact reviews. Click <strong>New Impact Review</strong> to open one.
                </TableCell></TableRow>
              )}
              {!loading && rows.map((ci) => {
                const sc = STATUS_COLOR[ci.status] || { bg: "#e9ecef", color: "#495057" };
                const rc = RISK_COLOR[ci.risk_level] || { bg: "#e9ecef", color: "#495057" };
                return (
                  <TableRow key={ci.id} hover sx={{ cursor: "pointer" }} onClick={() => navigate(`/change-impacts/${ci.id}`)}>
                    <TableCell><strong>#{ci.id}</strong></TableCell>
                    <TableCell>Job #{ci.job_id}</TableCell>
                    <TableCell>Doc #{ci.document_id} → {ci.trigger_drawing_revision}</TableCell>
                    <TableCell><Chip size="small" label={ci.risk_level} sx={{ backgroundColor: rc.bg, color: rc.color, fontWeight: 600, fontSize: "0.7rem" }} /></TableCell>
                    <TableCell><Chip size="small" label={prettyStatus(ci.status)} sx={{ backgroundColor: sc.bg, color: sc.color, fontWeight: 600, fontSize: "0.7rem" }} /></TableCell>
                    <TableCell>{ci.disposition || "—"}</TableCell>
                    <TableCell>{ci.hold_active ? <Chip size="small" label="HOLD" color="error" /> : "—"}</TableCell>
                    <TableCell>{ci.created_at ? new Date(ci.created_at).toISOString().slice(0, 10) : "—"}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </SmartTable>
        </TableContainer>
      </Container>
    </>
  );
}
